/**
 * Carbon Performance — Prestige sticky purchase bar enhancement.
 * Registers product-sticky-bar before theme.js so native observer logic is not used.
 */
class CarbonProductStickyBar extends HTMLElement {
  #buyButtons = null;
  #observer = null;
  #onResize = null;
  #onScroll = null;
  #scrollRaf = null;

  connectedCallback() {
    this.#buyButtons = document.querySelector(
      "[data-product-buy-buttons] button[type='submit']"
    );

    if (!this.#buyButtons) {
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];

        if (!entry) {
          return;
        }

        this.#setVisible(this.#shouldShowSticky());
      },
      {
        threshold: 0,
        rootMargin: "0px"
      }
    );

    this.#observer.observe(this.#buyButtons);
    this.#onResize = () => this.#evaluate();
    window.addEventListener("resize", this.#onResize, { passive: true });
    this.#onScroll = () => {
      if (this.#scrollRaf) {
        return;
      }

      this.#scrollRaf = window.requestAnimationFrame(() => {
        this.#scrollRaf = null;
        this.#evaluate();
      });
    };
    window.addEventListener("scroll", this.#onScroll, { passive: true });

    const formId = this.getAttribute("form");

    if (formId && document.forms[formId]) {
      document.forms[formId].addEventListener("product:rerender", () => {
        window.requestAnimationFrame(() => this.#rebindTarget());
      });
    }

    this.#evaluate();
    window.requestAnimationFrame(() => this.#evaluate());
  }

  #rebindTarget() {
    const target = document.querySelector(
      "[data-product-buy-buttons] button[type='submit']"
    );

    if (!target || target === this.#buyButtons) {
      this.#evaluate();
      return;
    }

    this.#observer?.disconnect();
    this.#buyButtons = target;
    this.#observer.observe(this.#buyButtons);
    this.#evaluate();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;

    if (this.#onResize) {
      window.removeEventListener("resize", this.#onResize);
      this.#onResize = null;
    }

    if (this.#onScroll) {
      window.removeEventListener("scroll", this.#onScroll);
      this.#onScroll = null;
    }

    if (this.#scrollRaf) {
      window.cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = null;
    }
  }

  #isMainButtonClearlyVisible(rect) {
    return rect.bottom > 56 && rect.top < window.innerHeight - 56;
  }

  #shouldShowSticky() {
    if (!this.#buyButtons) {
      return false;
    }

    const rect = this.#buyButtons.getBoundingClientRect();
    const isButtonVisible = this.#isMainButtonClearlyVisible(rect);

    if (isButtonVisible) {
      return false;
    }

    const isMobile = window.matchMedia("(max-width: 699px)").matches;

    if (isMobile) {
      return window.scrollY > 120 && !isButtonVisible;
    }

    return true;
  }

  #evaluate() {
    if (!this.#buyButtons) {
      return;
    }

    this.#setVisible(this.#shouldShowSticky());
  }

  #setVisible(visible) {
    this.classList.toggle("is-visible", visible);
    this.setAttribute("aria-hidden", visible ? "false" : "true");
  }
}

if (!window.customElements.get("product-sticky-bar")) {
  window.customElements.define("product-sticky-bar", CarbonProductStickyBar);
}
